/**
 * Types for all data definition within this application or from external APIs
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 * @author Carlos Romera <cromera@adasasistemas.com>
 */

// Library
import React, { ReactElement } from 'react';

/**
 * Expected props for all icon components within the icon folder
 */
export interface Icon {
	customStyles: string;
	disabled?: boolean;
	error?: boolean;
	handleClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

/**
 * Expected data object for dropdown list within the submit form
 */
export interface ReportOptionType {
	option: string;
	noID: boolean;
}

/**
 * Expected data object that /reportissue expected to recieve in payload
 */
export interface ReportErrorItemType {
	id: string;
	userID: string;
	message: string;
	reportDate: string;
	field?: string;
	entity?: {
		entityType?: ReportErrorEntityType;
		entityID?: string | null;
	};
}

export type ReportErrorEntityType =
	| 'Device'
	| 'MeterReading'
	| 'Authorisation'
	| 'PersonalDetail'
	| 'Work'
	| 'LegalEntity'
	| 'ContactToClient'
	| 'WaterAccount';

/**
 * Expected props for all layout components within the layout folder
 */
export type LayoutProps = ({ children }: { children: ReactElement }) => ReactElement;

/**
 * Expected props for all page components within the page folder
 */
export type PageComponent = { (): ReactElement; layout: LayoutProps };

/**
 * Expected data object for legal entities
 */
export interface ClientType {
	id: string;
	reference: string;
	name: string;
	legalName?: string;
	mailingName?: string;
	streetAddress?: Address;
	postalAddress: Address;
	primaryContact?: string;
	contactToClient?: ClientContact;
}

export interface ClientContact {
	id: string;
	clientId: string;
	firstName: string;
	lastName: string;
	middleName: string;
	preferredContactPhone: string;
	mobile: string;
	alternatePhone: string;
	streetAddress: Address;
	postalAddress: Address;
	email: string;
	emailVerified: boolean;
	preferredContactMethod: ContactMethod;
	clientRoles: ClientRole[];
}

export enum ContactMethod {
	ANY = 'Any',
	EMAIL = 'Email',
	POST = 'Post',
}

interface ClientRole {
	name: string;
	role: string;
}

/**
 * Expected data object for all addresses
 */
export interface Address {
	id: string;
	addressLine1: string;
	addressLine2?: string | null;
	suburb: string;
	postalCode: string;
	state: string;
	country: string;
}

export enum AddressType {
	STREET = 'STREET',
	POSTAL = 'POSTAL',
}

/**
 * Expected data object for notifications from the API
 * @interface
 */
export interface NotificationType {
	/** Unique ID of the notification, should be a `UUID` or `GUID` string, generated from the CRM  */
	id: string;
	/** Title of the notification */
	title: string;
	/** Content of the notification */
	content: string | undefined;
	/** date of publication of the notification */
	published: string;
	/** Has the notification been read by the user*/
	read: boolean;
	/** Date and time when the notification has been read*/
	readAt: string;
	/** Full notification content */
	data?: NotificationDataType;
}

export interface NotificationDataType {
	body: string;
	links: Array<NotificationLinkType>;
}

export interface NotificationLinkType {
	keyword: string;
	url: string;
}

// TODO: update Authorisation model according to latest version of water-user.swagger.yaml file
// TODO: rename as Authorisation because it is confusing (it is not about authentication)
/**
 * Expected data object for authorisation from the API
 * @interface
 */
export interface AuthType {
	allocation?: string;
	authorisationType: string;
	detailId: string;
	expiryDate: string;
	id: string;
	landDescription?: string;
	location: string;
	managementGroupArea: string;
	maxAreaHa: string;
	maximumRates: string;
	office: string;
	purpose: string;
	reference: string;
	ropLocationId?: string;
	source: string;
	sourceLocation: SourceLocationType[];
	status: string;
	statutoryClass: string;
	conditions?: AuthConditionType[];
	otherCondition?: string;
	typeCode: string;
	volume: number;
	maxVolumeToBeTaken: number;
	volumetricLimit?: string;
	waterPlan: string;
	attachedLands: AuthAttachedLand[];
	managementSubgroup: string;
	additionalDescriptions: WaterAdditionalDescription[];
	place: string;
	volumetricLimits: AuthVolumetricLimit[];
	waterAllocationGroup: string;
	priority: string;
	capacity: string;
	capacityOfExcavation: string;
}

export interface AuthorisationSummary {
	wateraccountId: string;
	authaccountId: string;
	id: string;
	status?: string;
	allocation?: string;
	authorisationType?: string;
	reference?: string;
	typeCode: string;
	managementSubgroup?: string;
}

/**
 * A summary of a Device for list views.
 */
export interface DeviceSummary {
	id: string;
	clientId?: string; // attribute on client-side only
	deviceStatus: DeviceType.deviceStatus;
	displayName?: string;
	reference: string;
	make?: string;
	model?: string;
	type?: string;
	deviceDescription?: string;
	lastReadingDate?: string;
	qrCode?: string;
	waterManagementArea?: WaterManagementAreaType;
	connectedDevices?: ConnectedDeviceSummary[];
}

/**
 * Summary details of the Connected Device
 */
export interface ConnectedDeviceSummary {
	id: string;
	deviceStatus?: DeviceType.deviceStatus;
	displayName?: string;
	reference: string;
	serialNumber: string;
	make?: string;
	model?: string;
	type: string;
	deviceDescription?: string;
	lastReadingDate?: string;
	qrCode?: string;
}

// TODO: update model
/**
 * Expected data object for device (water meter) from the API
 * @interface
 */
export interface DeviceType {
	id: string;
	deviceStatus: DeviceType.deviceStatus;
	displayName?: string;
	reference: string;
	dialsType?: DialsTypeItem;
	serialNumber: string;
	make: string;
	model: string;
	type: string;
	deviceDescription: string;
	size: number;
	sizeType: DeviceType.sizeType;
	installationDate: string;
	width?: number;
	widthUnits: DeviceType.widthUnits;
	removalDate: string;
	certificationExpiryDate?: string;
	lastReadingDate?: string;
	qrCode?: string;
	gpsLocation?: GPSLocationItem;
	waterManagementArea: WaterManagementAreaType;
	connectedDevices?: ConnectedDeviceSummary[];
}

export interface DialsTypeItem {
	dialsType: string;
	effectiveDate: string;
}

export interface GPSLocationItem {
	latitude?: number;
	longitude?: number;
	datumType?: string;
}

/**
 * Water Management Area Types
 */
export enum WaterManagementAreaType {
	SUPPLEMENTED = 'supplemented',
	UNSUPPLEMENTED = 'unsupplemented',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DeviceType {
	/**
	 * Status of the device ( In Service, Unknown, Fault Reported, Faulty, Removed )
	 */
	export enum deviceStatus {
		IN_SERVICE = 'In Service',
		UNKNOWN = 'Unknown',
		FAULT_REPORTED = 'Fault Reported',
		FAULTY = 'Faulty',
		REMOVED = 'Removed',
	}
	/**
	 * The type of the main device
	 */
	export enum type {
		MODULAR_METER = 'Modular Meter',
		SC_METER = 'SC Meter',
		WEIR_DISCHARGE = 'Weir Discharge',
		WL_STATION = 'WL Station',
		SENSOR = 'Sensor',
	}

	/**
	 * The installation type of the device
	 */
	export enum sizeType {
		FULL_PIPE = 'Full Pipe',
		PARTIALLY_FILLED_PIPE = 'Partially Filled Pipe',
		OPEN_CHANNEL = 'Open Channel',
	}

	/**
	 * The unit of the width of the device
	 */
	export enum widthUnits {
		MILLIMETERS_MM_ = 'Millimeters (mm)',
		METRES_M_ = 'Metres (m)',
	}
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ConnectedDeviceSummary {
	/**
	 * The type of connected device
	 */
	export enum type {
		LOGGER = 'Logger',
		SENSOR = 'Sensor',
		TELEMETRY = 'Telemetry',
	}
}

/**
 * Expected data object for work from the API
 * @interface
 */
export interface WorkItemType {
	id: string;
	reference: string | null;
	type: string | null;
	status: string | null;
	hydraulicType: string | null;
	artesianCondition: string | null;
	depth: string | null;
	localBoreName: string | null;
	pumpOrHeadwork: string | null;
	gwbRegistrationNo: string | null;
	pumpType: string | null;
	stroke: string | null;
	bore: string | null;
	outlet: string | null;
	inlet: string | null;
	dutyDischarge: string | null;
	dutyHead: string | null;
	motorType: string | null;
	workdetailsid: string | null;
}

/**
 * Water Management Area Types
 */
export enum WithdrawalReasonType {
	WRONG_DEVICE_SELECTED = 'Wrong Device selected',
	WRONG_NUMBER_ENTERED = 'Wrong number entered',
	WRONG_UNIT_OF_MEASURE_SELECTED = 'Wrong unit of measure selected',
	OTHER = 'Other',
}

/**
 * Expected data object for device reading from the API
 * @interface
 */
export interface DeviceReading {
	id?: string;
	readingDate: string;
	status?: string;
	withdrawalReason?: WithdrawalReasonType;
	processedReading: number;
	rawDialsReading: string;
	rawVariableReading?: string;
	unitOfMeasure?: string;
	take?: number;
	location?: string;
	readingSource?: DeviceReadingSource;
	selectionSource?: DeviceSelectionSource;
	sourceType?: ResourceSourceType;
	assessedTake?: number;
	assessedTakeReason?: string;
	readingType?: ReadingReason;
	deviceCondition?: string; // TODO: REMOVE
	// photoFileName?: string;	// TODO: ADD
	photoIdentifier?: string; // TODO: REMOVE?
}

/**
 * Enum for the meter reading status
 * @readonly
 * @enum {string}
 */
export enum ReadingStatus {
	VERIFIED = 'Verified',
	REQUIRES_REVIEW = 'Requires Review',
	IGNORE = 'Ignore',
	CANCELLED = 'Cancelled',
	SUBMITTED = 'Submitted',
	WITHDRAWN = 'Withdrawn',
}
export enum UnitSymbolLiteral {
	L = 'L',
	ML = 'ML',
	KL = 'KL',
	M3 = 'm3',
	X10M3 = 'x10m3',
	X100M3 = 'x100m3',
}

export type UnitSymbol = `${UnitSymbolLiteral}`;

export interface MeasurementUnit {
	symbol: UnitSymbol;
	name: string;
}

export const MeasurementUnits: { [key: string]: MeasurementUnit } = {
	L: { symbol: UnitSymbolLiteral.L, name: 'Liters' },
	ML: { symbol: UnitSymbolLiteral.ML, name: 'Megalitres' },
	kL: { symbol: UnitSymbolLiteral.KL, name: 'Kilolitres' },
	m3: { symbol: UnitSymbolLiteral.M3, name: 'Cubic meters' },
	x10m3: { symbol: UnitSymbolLiteral.X10M3, name: 'Cubic meters x10' },
	x100m3: { symbol: UnitSymbolLiteral.X100M3, name: 'Cubic meters x100' },
};

/**
 * Enum for the meter reading selection source as metadata
 * @readonly
 * @enum {string}
 */
export enum DeviceSelectionSource {
	QR = 'QRcode',
	MANUAL = 'Manualselection',
}

/**
 * Enum for the meter reading reading source as metadata
 * @readonly
 * @enum {string}
 */
export enum DeviceReadingSource {
	OCR = 'OCRonly',
	OVERRIDE = 'OCRmanualoverride',
	MANUAL = 'Manualreading',
}

/**
 * Enum for the meter reading client source as metadata
 * @readonly
 * @enum {string}
 */
export enum ResourceSourceType {
	DEPT = 'WMS',
	APP = 'WaterIQApp',
	WEB = 'WaterIQPortal',
	APP_2 = 'Water IQ App',
	WEB_2 = 'Water IQ Portal',
}
export type SourceType = Omit<
	ResourceSourceType,
	ResourceSourceType.WEB_2 | ResourceSourceType.APP_2
>;

/**
 * The reason for the reading
 */
export enum ReadingReason {
	REQUIRED = 'Required',
	SPECIAL_REQUEST = 'Special request',
	END_OF_WATER_YEAR = 'End of water year',
	INTERIM = 'Interim',
	CLIENT_READ = 'Client read',
}

export interface UserIdentityType {
	userId: string;
	firstName?: string;
	lastName?: string;
	email: string;
	birthdate?: string;
}

export interface Linking {
	waterUserId: string;
	reportedIssueId?: string;
	title: string;
	dateSubmitted: string;
	reference: string;
	status: string;
}

// TODO: rename as WaterUser or UserAccount
/**
 * Expected data object for account details from the API
 * @interface
 */
export interface AccountType {
	id: string;
	email: string;
	emailVerified: boolean;
	firstName: string;
	middleName: string;
	lastName: string;
	birthdate: string;
	title?: string;
	streetAddress?: Address;
	postalAddress: Address;
	mobile: string;
	alternatePhone: string;
	preferredContactPhone: string;
	locations?: Location[];
	isCurrentTandC: boolean;
	acceptedTandC?: TandCSummary;
	clients: ClientType[] | null;
}

export interface DqpAccountType {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	mobile: string;
	isCurrentTandC: boolean;
	acceptedTandC?: TandCSummary;
}

/**
 * Expected data object for termsAndConditions from the API, which is used in the account
 * @interface
 */
export interface TermsAndConditionsType {
	content: string;
	id: string;
	startDate: string;
	version: string;
}

export interface Announcement {
	announcedAllocationId: string;
	waterYear: string;
	allocationPercentage: number;
	authorisationVolume: number;
	maxVolume: number;
	effectiveDate: string;
	statusReason: string;
	authorisationReference: string;
}

/**
 * Pagination wrapper with main data items and metadata
 */
export interface Page<T> {
	items: T[];
	metadata: {
		totalCount: number;
		limit: number;
		offSet: number;
	};
}

// TODO: if WEH API would get renamed as page.metadata.offset or [...].offSet – to harmonize both WEH and DQP APIs – then rename this as Page and remove above
export interface PageDqp<T> {
	items: T[];
	metadata: {
		totalCount: number;
		limit: number;
		offset: number;
	};
}

/**
 * Expected data object for water accounts from the API
 * @interface
 */
export interface WaterAccountType {
	wateraccountId: string;
	accountName: string;
	accountReference: string;
	managementGroup: string;
	managementSubGroup: string;
	devices: WADevice[];
	works: WAWork[];
	meterCases: WAMeterCase[];
	recentAnnouncedAllocations: Announcement[];
}
interface WADevice {
	deviceReferenceId: string;
	reference: string;
	make: string;
	model: string;
	deviceType: string;
}

interface WAWork {
	workDetailId: string;
	workReferenceId: string;
	reference: string;
	status: string;
	type: string;
}
export interface WAMeterCase {
	meterCaseCode: string;
	meterManagementProjectType: string;
	deviceReference: string;
	waterAccountReference: string;
	responseDueDate: string;
	caseType: string;
}

/**
 * Expected data object for location from the API, which is used in the account
 * @interface
 */
interface Location {
	/** Unique ID of the Location, should be a `UUID` or `GUID` string, generated from the CRM  */
	id: string | number;
	/** Description of the Location */
	description: string;
	/** Related management area for this location */
	waterManagementAreaItem: WaterManagementArea;
	/** Internal reference (similar to an ID) managed by the department */
	reference: string;
}

interface TandCSummary {
	id: string;
	termsId: string;
	version: string;
	auditDate: string;
}

/**
 * Expected data object for water management from the API, which is used in the account
 * @interface
 */
interface WaterManagementArea {
	/** Unique ID of the management Area, should be a `UUID` or `GUID` string, generated from the CRM  */
	id: string | number;
	/** Internal reference (similar to an ID) managed by the department */
	reference: string;
	/** Description of the management area */
	description: string | null;
}

/**
 * Enum for the different ApiStatus.
 * @readonly
 * @enum {string}
 */
export enum ApiStatus {
	/** The API is called and no response has been given yet */
	IDLE = 'idle',
	/** The API is called and no response has been given yet */
	PENDING = 'pending',
	/** The API return a non empty response */
	FULFILLED = 'fulfilled',
	/** The API return an empty response */
	EMPTY = 'empty',
	/** The API return an error */
	ERROR = 'error',
}

/**
 * Expected data object for source location from the API
 * @interface
 */
export interface SourceLocationType {
	lot: string;
	plan: string;
	atsTitle: string;
	land: string;
}

interface AuthAttachedLand {
	attachedLandId: string;
	attachedLand: string;
}

interface AuthVolumetricLimit {
	volumetricLimitId: string;
	volumetricLimit: string;
}

interface WaterAdditionalDescription {
	waterAdditionalDescription: string;
}

/**
 * Expected data object element for Authorisation conditions from the API
 */
export interface AuthConditionType {
	defType: string;
	reference: string;
	result: string;
}

/**
 * Status that represent the state of a component's http request
 * @interface
 */
export interface RequestStatus {
	status: ApiStatus;
	statusCode: number;
}

/**
 * Information that are needed for creating item on sidebar
 */
export interface SideBarItemType {
	name: string;
	icon?: string | null;
	url: string;
	id?: string;
	depth?: number;
	allowURLParams?: boolean;
	subItems?: SideBarItemType[];
	disabled: boolean;
}

// TODO: doc
export enum AccountSubpage {
	ACCOUNT_DETAILS = 'accountDetails',
	CLIENT_DETAILS = 'clientDetails',
	CONTACT_DETAILS = 'contactDetails',
	LINK_ADDITIONAL_CLIENT = 'linkAdditionalClient',
	REGISTER_AS_WEH = 'registerAsWEH',
	CLOSE_ACCOUNT_AS_DQP = 'closeAccountAsDQP',
}

export enum FileUploadStatus {
	REQUESTED = 'requested',
	CLEAN = 'Clean',
	DIRTY = 'Dirty',
	FAILED = 'Failed',
	RESTORED = 'Restored',
}

export enum FileUploadValidationError {
	MAX_FILE_SIZE = 'isValidFileSize',
	INVALID_FILE_FORMAT = 'isValidFileType',
}

export enum FileUploadValidationErrorMessage {
	MAX_FILE_SIZE = 'Max file size exceeded',
	INVALID_FILE_FORMAT = 'Invalid file format',
	UNKNOWN_ERROR = 'An unknown error occurred',
}

export enum FileUploadAPIError {
	UPLOAD_FAILED = 'Upload failed',
	MALWARE_DETECTED = 'File contains malware',
	CONNECTION_TIMEOUT = 'Connection timeout',
}

export interface MDSummaryItem {
	id: string;
	deviceStatus?: DeviceType.deviceStatus;
	displayName?: string;
	reference: string;
	serialNumber?: string;
	type: string;
	deviceDescription?: string;
}

export interface ReadingRequestItem {
	id: string;
	mdReference?: MDSummaryItem;
	readingDueDate: string;
	clientId: string;
}
